import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// ============================================================================
// PROVIDER UTILITIES (added for 1.8.0 — DeepSeek, HuggingFace support)
// ============================================================================

/**
 * Detect provider from model ID string.
 * Used to color-code and label providers consistently across the dashboard.
 */
export function getProviderFromModel(modelId) {
  if (!modelId) return "unknown";
  const m = modelId.toLowerCase();
  if (m.startsWith("gpt-") || m.startsWith("o1-") || m.startsWith("o3-") || m.startsWith("tts-") || m.startsWith("whisper-")) return "openai";
  if (m.startsWith("claude-")) return "anthropic";
  if (m.startsWith("gemini-") || m.startsWith("models/gemini")) return "google";
  if (m.startsWith("deepseek-")) return "deepseek";
  if (m.includes("/")) return "huggingface"; // org/model format
  return "unknown";
}

/**
 * Consistent brand colors per provider.
 * Falls back to zinc for unknown providers.
 */
export const PROVIDER_COLORS = {
  openai:      { hex: "#10b981", bg: "bg-emerald-500/15", text: "text-emerald-400", border: "border-emerald-500/20" },
  anthropic:   { hex: "#f97316", bg: "bg-orange-500/15",  text: "text-orange-400",  border: "border-orange-500/20"  },
  google:      { hex: "#3b82f6", bg: "bg-blue-500/15",    text: "text-blue-400",    border: "border-blue-500/20"    },
  deepseek:    { hex: "#8b5cf6", bg: "bg-violet-500/15",  text: "text-violet-400",  border: "border-violet-500/20"  },
  huggingface: { hex: "#f59e0b", bg: "bg-amber-500/15",   text: "text-amber-400",   border: "border-amber-500/20"   },
  unknown:     { hex: "#71717a", bg: "bg-zinc-500/15",    text: "text-zinc-400",    border: "border-zinc-500/20"    },
};

/**
 * Smart cost formatter.
 * - Very small costs (< $0.001): 6 decimal places
 * - Small costs (< $0.01): 4 decimal places
 * - Normal costs: 4 decimal places
 * Handles audio/image/embedding modalities correctly.
 */
export function formatCost(cost, unit = "usd") {
  if (cost == null || isNaN(cost)) return "$0.00";
  if (cost === 0) return "$0.00";
  if (cost < 0.0001) return `$${cost.toFixed(6)}`;
  if (cost < 0.001)  return `$${cost.toFixed(6)}`;
  if (cost < 0.01)   return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(4)}`;
}

/**
 * ProviderBadge — inline JSX-free helper that returns badge class strings.
 * Use with the PROVIDER_COLORS map.
 */
export function getProviderBadgeClasses(modelId) {
  const provider = getProviderFromModel(modelId);
  const colors = PROVIDER_COLORS[provider] || PROVIDER_COLORS.unknown;
  return {
    provider,
    className: `text-xs px-1.5 py-0.5 rounded font-mono ${colors.bg} ${colors.text} ${colors.border} border`,
    hex: colors.hex,
  };
}

/**
 * HuggingFace task type → modality label.
 * Maps the 17 PATCHED_METHODS to their display modality.
 */
export const HF_TASK_MODALITY = {
  chat_completion: "text",
  text_generation: "text",
  translation: "text",
  summarization: "text",
  fill_mask: "text",
  table_question_answering: "text",
  automatic_speech_recognition: "audio",
  text_to_speech: "audio",
  audio_classification: "audio",
  text_to_image: "image",
  image_to_text: "image",
  image_classification: "image",
  image_segmentation: "image",
  object_detection: "image",
  feature_extraction: "embedding",
  text_classification: "classification",
  token_classification: "classification",
};

/**
 * Resolve modality from trace data.
 * Falls back to task_type → HF_TASK_MODALITY if modality field is empty.
 */
export function resolveModality(trace) {
  if (trace?.modality && trace.modality !== "") return trace.modality.toLowerCase();
  if (trace?.task_type && HF_TASK_MODALITY[trace.task_type]) return HF_TASK_MODALITY[trace.task_type];
  return "text";
}
